import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Breadcrumbs,
  Button,
  Stack,
  Typography,
  TextField,
  FormControlLabel,
  Switch,
  Box,
  InputAdornment,
  Grid,
  Tab,
  FormControl,
  FormLabel,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormGroup,
  ListItemText,
  Divider,
  CircularProgress,
  Radio,
  RadioGroup,
  Modal,
  Avatar,
  ImageListItem,
  ImageListItemBar,
  IconButton,
} from "@mui/material";
import ViewListIcon from "@mui/icons-material/ViewList";
import GridViewIcon from "@mui/icons-material/GridView";
import DeleteIcon from "@mui/icons-material/Delete";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import useJumboAuth from "@jumbo/hooks/useJumboAuth";
import Div from "@jumbo/shared/Div";
import Images from "app/pages/media/images/Images";
import { PROD_URL } from "app/services/config";
import { sanitizeLogoUrlString } from "app/pages/customers/dsok/utils/sanitizeLogoUrlString";
import {
  getSettings,
  saveSettings,
} from "app/data/dailyMenuScreenSettings.storage";
import {
  getDailyMenuSettings,
  updateDailyMenuSettings,
  mailchimpTestConnection,
} from "app/services/api/dailyMenu";
import {
  DAILY_MENU_TYPES,
  DAILY_MENU_TEMPLATES,
} from "app/utils/constants/settings";

/** Predvolený (placeholder) hlavný názov podľa jazyka – rovnaká logika ako na obrazovke denného menu. */
function getDefaultMainTitle(code) {
  return String(code || "")
    .toLowerCase()
    .startsWith("sk")
    ? "Denné menu"
    : "Daily menu";
}

function normalizeCustomerLanguages(customer, t) {
  const raw =
    customer?.productLanguages ||
    customer?.dsokSettings?.customer?.languages ||
    [];
  if (!Array.isArray(raw) || raw.length === 0) {
    return [
      { code: "sk-SK", label: "Slovenčina" },
      { code: "en-US", label: "English" },
    ];
  }
  return raw.map((item) => {
    if (typeof item === "string") {
      return {
        code: item,
        label: t(`languages.${item}`, { defaultValue: item }),
      };
    }
    return {
      code: item.code || item,
      label: item.label || item.name || item.code || String(item.code || item),
    };
  });
}

const MIN_INTERVAL_SEC = 1;
const MIN_MEALS_PER_PAGE = 1;
/** Minimálny interval kontroly typu menu (raňajky/obed/večera) na obrazovke – minúty. */
const MIN_MENU_TYPE_CHECK_MIN = 5;

const TABS = [
  {
    value: "basic",
    labelKey: "pages.dsokDailyMenu.tabBasic",
    defaultLabel: "Základné nastavenia",
  },
  {
    value: "rotation",
    labelKey: "pages.dsokDailyMenu.tabRotation",
    defaultLabel: "Rotácia",
  },
  {
    value: "display",
    labelKey: "pages.dsokDailyMenu.tabDisplay",
    defaultLabel: "Zobrazenie",
  },
  {
    value: "menuTypes",
    labelKey: "pages.dsokDailyMenu.tabMenuTypes",
    defaultLabel: "Typy menu",
  },
  {
    value: "ticker",
    labelKey: "pages.dsokDailyMenu.tabTicker",
    defaultLabel: "Info text",
  },
  {
    value: "webExport",
    labelKey: "pages.dsokDailyMenu.tabExport",
    defaultLabel: "Export",
  },
  {
    value: "integrations",
    labelKey: "pages.dsokDailyMenu.tabIntegrations",
    defaultLabel: "Integrácie",
  },
];

/** Časti, z ktorých sa skladá názov jedla pri CSV exporte (multiselect „Tvorba názvu jedla“). */
const CSV_NAME_PARTS = [
  {
    code: "weight",
    labelKey: "pages.dsokDailyMenu.csvNamePartWeight",
    defaultLabel: "Váha",
  },
  {
    code: "allergens",
    labelKey: "pages.dsokDailyMenu.csvNamePartAllergens",
    defaultLabel: "Alergény",
  },
];

/** Default zloženie názvu jedla (obe časti zapnuté). */
const CSV_NAME_COMPOSITION_DEFAULT = ["weight", "allergens"];

/** Dni týždňa (rovnaké poradie ako DAYS v app/utils/constants/settings) so slovenskými labelmi. */
const WEB_EXPORT_DAYS = [
  { code: "monday", labelKey: "pages.dsokDailyMenu.days.monday", defaultLabel: "Pondelok" },
  { code: "tuesday", labelKey: "pages.dsokDailyMenu.days.tuesday", defaultLabel: "Utorok" },
  { code: "wednesday", labelKey: "pages.dsokDailyMenu.days.wednesday", defaultLabel: "Streda" },
  { code: "thursday", labelKey: "pages.dsokDailyMenu.days.thursday", defaultLabel: "Štvrtok" },
  { code: "friday", labelKey: "pages.dsokDailyMenu.days.friday", defaultLabel: "Piatok" },
  { code: "saturday", labelKey: "pages.dsokDailyMenu.days.saturday", defaultLabel: "Sobota" },
  { code: "sunday", labelKey: "pages.dsokDailyMenu.days.sunday", defaultLabel: "Nedeľa" },
];

const DsokDailyMenuScreenSettings = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { getAuthUser } = useJumboAuth();
  const customer = getAuthUser()?.customer;
  const customerId = getAuthUser()?.customerId;
  const [logoModalOpen, setLogoModalOpen] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState(null);

  const enableWebExport =
    customer?.dsokSettings?.customer?.customerUISettings
      ?.enableDailyMenuWebExport === true;
  // Export tab je vždy dostupný (kvôli CSV nastaveniam). Sekcia „Web“ vnútri sa zobrazí
  // len ak má zákazník zapnutý web export.
  const tabs = TABS;

  const languages = useMemo(
    () => normalizeCustomerLanguages(customer, t),
    [customer, t],
  );

  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [selectedTab, setSelectedTab] = useState("basic");

  // Kódy jazykov, ktoré má zákazník nakonfigurované (dostupné možnosti výberu).
  const availableLanguageCodes = useMemo(
    () => new Set(languages.map((l) => l.code)),
    [languages],
  );

  const selectedLanguageCodes = useMemo(() => {
    const s = String(form?.languages || "").trim();
    const parsed = s ? s.split("_").filter(Boolean) : [];
    // Ponechaj len jazyky, ktoré má zákazník nakonfigurované – odfiltruj stale (napr. "en",
    // ak zákazník angličtinu nemá). Zabráni jeho zobrazeniu v rotácii aj na obrazovke.
    return parsed.filter((c) => availableLanguageCodes.has(c));
  }, [form?.languages, availableLanguageCodes]);

  const handleLanguagesChange = (codes) => {
    update("languages", Array.isArray(codes) ? codes.join("_") : "");
  };

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const fromBackend = await getDailyMenuSettings();
        if (cancelled) return;
        const defaults = getSettings();
        const merged = {
          ...defaults,
          ...(fromBackend?.settings ?? fromBackend),
        };
        // Migrácia: starý spoločný productDisplayType → samostatne landscape/portrait.
        if (merged.productDisplayType != null) {
          const val = merged.productDisplayType === "grid" ? "grid" : "list";
          if (merged.productDisplayTypeLandscape == null)
            merged.productDisplayTypeLandscape = val;
          if (merged.productDisplayTypePortrait == null)
            merged.productDisplayTypePortrait = val;
          delete merged.productDisplayType;
        }
        setForm(JSON.parse(JSON.stringify(merged)));
      } catch (_) {
        if (cancelled) return;
        const settings = getSettings();
        setForm(JSON.parse(JSON.stringify(settings)));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const update = (key, value) => {
    setForm((prev) => (prev ? { ...prev, [key]: value } : prev));
    if (errors[key]) setErrors((e) => ({ ...e, [key]: null }));
  };

  const updateNested = (key, subKey, value) => {
    setForm((prev) => {
      if (!prev) return prev;
      const next = { ...(prev[key] || {}) };
      next[subKey] = value;
      return { ...prev, [key]: next };
    });
  };

  const validate = () => {
    const e = {};
    if (!form) return e;
    if (Number(form.languageRotationIntervalSec) < MIN_INTERVAL_SEC)
      e.languageRotationIntervalSec = t("pages.dsokDailyMenu.minIntervalSec", {
        defaultValue: "Min 1 s",
      });
    if (Number(form.pageRotationIntervalSec) < MIN_INTERVAL_SEC)
      e.pageRotationIntervalSec = t("pages.dsokDailyMenu.minIntervalSec", {
        defaultValue: "Min 1 s",
      });
    if (
      form.weeklyMenuRotationEnabled &&
      Number(form.weeklyMenuRotationIntervalSec) < MIN_INTERVAL_SEC
    )
      e.weeklyMenuRotationIntervalSec = t("pages.dsokDailyMenu.minIntervalSec", {
        defaultValue: "Min 1 s",
      });
    if (
      form.menuTypeCheckIntervalMin != null &&
      form.menuTypeCheckIntervalMin !== "" &&
      Number(form.menuTypeCheckIntervalMin) < MIN_MENU_TYPE_CHECK_MIN
    )
      e.menuTypeCheckIntervalMin = t("pages.dsokDailyMenu.minMenuTypeCheckMin", {
        defaultValue: "Min 5 min",
      });
    if (Number(form.maxMealsPerPageLandscape) < MIN_MEALS_PER_PAGE)
      e.maxMealsPerPageLandscape = t("pages.dsokDailyMenu.minOne", {
        defaultValue: "Must be ≥ 1",
      });
    if (Number(form.maxMealsPerPagePortrait) < MIN_MEALS_PER_PAGE)
      e.maxMealsPerPagePortrait = t("pages.dsokDailyMenu.minOne", {
        defaultValue: "Must be ≥ 1",
      });
    if (selectedLanguageCodes.length === 0)
      e.languages = t("pages.dsokDailyMenu.languagesRequired", {
        defaultValue: "Required",
      });
    setErrors(e);
    return e;
  };

  const handleSave = async () => {
    if (!form) return;
    const err = validate();
    if (Object.keys(err).length > 0) return;
    setSaving(true);
    try {
      // Ulož len jazyky, ktoré má zákazník nakonfigurované (odstráni stale napr. "en").
      const payload = { ...form, languages: selectedLanguageCodes.join("_") };
      await updateDailyMenuSettings(payload);
      saveSettings(payload);
      setForm(payload);
      enqueueSnackbar(
        t("pages.dsokDailyMenu.settingsSaved", {
          defaultValue: "Settings saved",
        }),
        {
          variant: "success",
        },
      );
    } catch (err) {
      enqueueSnackbar(
        t("notifications.wasFailed", { defaultValue: "Save failed" }),
        {
          variant: "error",
        },
      );
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    navigate("/customer/dsok/daily-menu");
  };

  if (loading || !form) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const pageTitle = t("pages.dsokDailyMenu.screenSettings", {
    defaultValue: "Nastavenie denného menu",
  });

  const fieldSx = { mt: 2, mb: 1, mx: 0.5 };
  const gridItemProps = { md: 6, lg: 4, xs: 12 };

  // Aktuálne zloženie názvu jedla pre CSV (default = obe časti zapnuté).
  const csvNameComposition = Array.isArray(form.csvExport?.nameComposition)
    ? form.csvExport.nameComposition
    : CSV_NAME_COMPOSITION_DEFAULT;

  return (
    <>
      <Breadcrumbs
        aria-label="breadcrumb"
        sx={{
          mb: 2,
          "& a": { color: "text.secondary", textDecoration: "none" },
          "& a:hover": { textDecoration: "underline" },
        }}
      >
        <Link to="/">{t("sidebar.menuItem.home")}</Link>
        <Link to="/customer/dsok/daily-menu">
          {t("sidebar.menuItem.dsokDailyMenu", { defaultValue: "Daily Menu" })}
        </Link>
        <Typography color="text.primary">{pageTitle}</Typography>
      </Breadcrumbs>

      <Div sx={{ display: "flex", width: "100%", p: 1 }}>
        <Box
          sx={{
            width: "100%",
            backgroundColor: "white",
            borderRadius: 1,
            boxShadow: 1,
            p: 2,
          }}
        >
          <Typography variant="h4" pl={2} pb={2} mt={0}>
            {pageTitle}
          </Typography>

          <TabContext value={selectedTab}>
            <Stack sx={{ mb: 2, maxHeight: "100%" }}>
              <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                <TabList onChange={(_, val) => setSelectedTab(val)}>
                  {tabs.map((tab) => (
                    <Tab
                      key={tab.value}
                      label={t(tab.labelKey, {
                        defaultValue: tab.defaultLabel,
                      })}
                      value={tab.value}
                    />
                  ))}
                </TabList>
              </Box>

              <TabPanel
                value="basic"
                sx={{ pb: 1, pr: 0, maxHeight: "60vh", overflow: "auto" }}
              >
                <Stack spacing={3}>
                  <Box>
                    <Typography variant="h6" gutterBottom>
                      {t("pages.dsokDailyMenu.logo", { defaultValue: "Logo" })}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 2 }}
                    >
                      {t("pages.dsokDailyMenu.logoHint", {
                        defaultValue:
                          "Vyberte logo z obrázkov nahraných na disku. Zobrazí sa na obrazovke denného menu.",
                      })}
                    </Typography>
                    <ImageListItem
                      sx={{
                        "& .MuiImageListItemBar-root": {
                          transition: "visibility 0s, opacity 0.3s linear",
                          opacity: 0,
                        },
                        "&:hover .MuiImageListItemBar-root": {
                          visibility: "visible",
                          opacity: 1,
                        },
                        width: "fit-content",
                        height: "auto",
                      }}
                    >
                      <Avatar
                        src={form.logo || "/images/pages/not_found.svg"}
                        variant="rounded"
                        sx={{
                          height: "auto",
                          width: "100%",
                          maxWidth: 250,
                          objectFit: "contain",
                          cursor: "pointer",
                        }}
                        onClick={() => setLogoModalOpen(true)}
                      />
                      {form.logo && (
                        <ImageListItemBar
                          subtitle={
                            <Stack
                              direction="row"
                              alignItems="center"
                              justifyContent="flex-end"
                            >
                              <DeleteIcon
                                sx={{ cursor: "pointer" }}
                                onClick={() => update("logo", "")}
                              />
                            </Stack>
                          }
                        />
                      )}
                    </ImageListItem>
                  </Box>

                  <Divider />

                  <Box>
                    <Typography variant="h6" gutterBottom>
                      {t("pages.dsokDailyMenu.mainTitle", {
                        defaultValue: "Hlavný názov",
                      })}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 2 }}
                    >
                      {t("pages.dsokDailyMenu.mainTitleHint", {
                        defaultValue:
                          "Názov zobrazený nad dátumom na obrazovke denného menu. Ak pre daný jazyk nevyplníte text, použije sa predvolený názov.",
                      })}
                    </Typography>
                    <Grid container columnSpacing={2}>
                      {languages.map((lang) => (
                        <Grid item xs={12} sm={6} key={lang.code}>
                          <Div sx={{ ...fieldSx, width: "100%" }}>
                            <TextField
                              label={`${t("pages.dsokDailyMenu.mainTitle", { defaultValue: "Hlavný názov" })} (${lang.label})`}
                              placeholder={getDefaultMainTitle(lang.code)}
                              value={
                                (form.mainTitle && form.mainTitle[lang.code]) ||
                                ""
                              }
                              onChange={(e) =>
                                updateNested(
                                  "mainTitle",
                                  lang.code,
                                  e.target.value,
                                )
                              }
                              fullWidth
                              size="small"
                            />
                          </Div>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>

                  <Divider />

                  <Box>
                    <Typography variant="h6" gutterBottom>
                      {t("pages.dsokDailyMenu.template", {
                        defaultValue: "Vyber template",
                      })}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 2 }}
                    >
                      {t("pages.dsokDailyMenu.templateHint", {
                        defaultValue:
                          "Kliknutím vyberte vizuálnu šablónu obrazovky. Lupou zobrazíte náhľad v plnej veľkosti.",
                      })}
                    </Typography>
                    <Grid container spacing={2}>
                      {DAILY_MENU_TEMPLATES.map((tpl) => {
                        const selected = form.template === tpl.id;
                        return (
                          <Grid item key={tpl.id} xs={6} sm={4} md={3}>
                            <Box
                              onClick={() => update("template", tpl.id)}
                              sx={{
                                position: "relative",
                                cursor: "pointer",
                                border: "3px solid",
                                borderColor: selected
                                  ? "primary.main"
                                  : "divider",
                                borderRadius: 1,
                                overflow: "hidden",
                                transition: "border-color 0.15s ease",
                                "&:hover": { borderColor: "primary.light" },
                              }}
                            >
                              <img
                                src={tpl.image}
                                alt={tpl.name}
                                style={{
                                  display: "block",
                                  width: "100%",
                                  height: "auto",
                                }}
                              />
                              {selected && (
                                <CheckCircleIcon
                                  color="primary"
                                  sx={{
                                    position: "absolute",
                                    top: 4,
                                    left: 4,
                                    bgcolor: "background.paper",
                                    borderRadius: "50%",
                                  }}
                                />
                              )}
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setPreviewTemplate(tpl);
                                }}
                                sx={{
                                  position: "absolute",
                                  top: 4,
                                  right: 4,
                                  bgcolor: "rgba(0,0,0,0.5)",
                                  color: "#fff",
                                  "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
                                }}
                              >
                                <ZoomInIcon fontSize="small" />
                              </IconButton>
                            </Box>
                            <Typography
                              variant="caption"
                              sx={{
                                display: "block",
                                mt: 0.5,
                                textAlign: "center",
                              }}
                            >
                              {tpl.name}
                            </Typography>
                          </Grid>
                        );
                      })}
                    </Grid>
                  </Box>
                </Stack>

                <Modal
                  open={!!previewTemplate}
                  onClose={() => setPreviewTemplate(null)}
                >
                  <Div
                    sx={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      width: "85vw",
                      maxHeight: "90vh",
                      overflow: "auto",
                      bgcolor: "background.paper",
                      boxShadow: 24,
                      p: 3,
                      textAlign: "center",
                    }}
                  >
                    {previewTemplate && (
                      <>
                        <Typography variant="h6" sx={{ mb: 2 }}>
                          {previewTemplate.name}
                        </Typography>
                        <img
                          src={previewTemplate.image}
                          alt={previewTemplate.name}
                          style={{ maxWidth: "100%", height: "auto" }}
                        />
                        <Stack
                          direction="row"
                          spacing={2}
                          justifyContent="center"
                          sx={{ mt: 2 }}
                        >
                          <Button
                            variant="outlined"
                            onClick={() => setPreviewTemplate(null)}
                          >
                            {t("buttons.close", { defaultValue: "Zavrieť" })}
                          </Button>
                          <Button
                            variant="contained"
                            onClick={() => {
                              update("template", previewTemplate.id);
                              setPreviewTemplate(null);
                            }}
                          >
                            {t("buttons.select", { defaultValue: "Vybrať" })}
                          </Button>
                        </Stack>
                      </>
                    )}
                  </Div>
                </Modal>

                <Modal
                  open={logoModalOpen}
                  onClose={() => setLogoModalOpen(false)}
                >
                  <Div
                    sx={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      width: "75vw",
                      bgcolor: "background.paper",
                      boxShadow: 24,
                      p: 4,
                    }}
                  >
                    <Images
                      layout={"row-reverse"}
                      selectorMode
                      allowed={["jpg", "jpeg", "png", "svg"]}
                      customerIdOverride={customerId}
                      onCancel={() => setLogoModalOpen(false)}
                      onSelect={(image) => {
                        if (!customerId) {
                          enqueueSnackbar(
                            t("pages.dsokDailyMenu.logoMissingCustomerId", {
                              defaultValue:
                                "Nedá sa vybrať obrázok – chýba ID zákazníka.",
                            }),
                            { variant: "error" },
                          );
                          setLogoModalOpen(false);
                          return;
                        }
                        const imageUrl = sanitizeLogoUrlString(
                          `${PROD_URL}/uploads/files/${customerId}/Images/${image.filename}`,
                        );
                        update("logo", imageUrl);
                        setLogoModalOpen(false);
                      }}
                    />
                  </Div>
                </Modal>
              </TabPanel>

              <TabPanel
                value="rotation"
                sx={{ pb: 1, pr: 0, maxHeight: "60vh", overflow: "auto" }}
              >
                <Stack spacing={3}>
                  {/* Sekcia: Jazyky */}
                  <Box>
                    <Typography variant="h6" gutterBottom>
                      {t("pages.dsokDailyMenu.sectionLanguages", {
                        defaultValue: "Jazyky",
                      })}
                    </Typography>
                    <Alert severity="info" sx={{ mb: 2 }}>
                      {t("pages.dsokDailyMenu.sectionLanguagesInfo", {
                        defaultValue:
                          "Vyberte jazyky zobrazenia denného menu a či má prebiehať automatická zmena jazyka v pravidelných intervaloch. Pri viacerých jazykoch sa obsah (názvy, popisy, info text) strieda podľa zvoleného intervalu.",
                      })}
                    </Alert>
                    <Grid container columnSpacing={2}>
                      <Grid item {...gridItemProps}>
                        <Div sx={fieldSx}>
                          <FormControl
                            fullWidth
                            size="small"
                            error={!!errors.languages}
                          >
                            <InputLabel>
                              {t("pages.dsokDailyMenu.languages", {
                                defaultValue: "Jazyky",
                              })}
                            </InputLabel>
                            <Select
                              multiple
                              value={selectedLanguageCodes}
                              onChange={(e) =>
                                handleLanguagesChange(e.target.value)
                              }
                              label={t("pages.dsokDailyMenu.languages", {
                                defaultValue: "Jazyky",
                              })}
                              renderValue={(selected) =>
                                selected
                                  .map(
                                    (code) =>
                                      languages.find((l) => l.code === code)
                                        ?.label || code,
                                  )
                                  .join(", ")
                              }
                            >
                              {languages.map((lang) => (
                                <MenuItem key={lang.code} value={lang.code}>
                                  <Checkbox
                                    checked={
                                      selectedLanguageCodes.indexOf(lang.code) >
                                      -1
                                    }
                                  />
                                  <ListItemText primary={lang.label} />
                                </MenuItem>
                              ))}
                            </Select>
                            {(errors.languages ||
                              !selectedLanguageCodes.length) && (
                              <Typography
                                variant="caption"
                                color="error"
                                sx={{ mt: 0.5, ml: 1.5 }}
                              >
                                {errors.languages ||
                                  t("pages.dsokDailyMenu.languagesRequired", {
                                    defaultValue: "Vyberte aspoň jeden jazyk.",
                                  })}
                              </Typography>
                            )}
                          </FormControl>
                        </Div>
                      </Grid>
                      <Grid item {...gridItemProps}>
                        <Div sx={fieldSx}>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={!!form.languageRotationEnabled}
                                onChange={(e) =>
                                  update(
                                    "languageRotationEnabled",
                                    e.target.checked,
                                  )
                                }
                              />
                            }
                            label={t(
                              "pages.dsokDailyMenu.languageRotationEnabled",
                              {
                                defaultValue: "Rotácia jazykov",
                              },
                            )}
                          />
                        </Div>
                      </Grid>
                      <Grid item xs={12} sm="auto">
                        <Div
                          sx={{
                            ...fieldSx,
                            maxWidth: 200,
                            minWidth: 72,
                            width: "100%",
                          }}
                        >
                          <TextField
                            label={t(
                              "pages.dsokDailyMenu.languageRotationIntervalSec",
                              {
                                defaultValue: "Interval rotácie jazykov (s)",
                              },
                            )}
                            type="number"
                            value={form.languageRotationIntervalSec ?? 5}
                            onChange={(e) =>
                              update(
                                "languageRotationIntervalSec",
                                Number(e.target.value) || 0,
                              )
                            }
                            size="small"
                            inputProps={{ min: MIN_INTERVAL_SEC }}
                            error={!!errors.languageRotationIntervalSec}
                            helperText={errors.languageRotationIntervalSec}
                            sx={{ width: "100%" }}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  s
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Div>
                      </Grid>
                    </Grid>
                  </Box>

                  <Divider />

                  {/* Sekcia: Stránky */}
                  <Box>
                    <Typography variant="h6" gutterBottom>
                      {t("pages.dsokDailyMenu.sectionPages", {
                        defaultValue: "Stránky",
                      })}
                    </Typography>
                    <Alert severity="info" sx={{ mb: 2 }}>
                      {t("pages.dsokDailyMenu.sectionPagesInfo", {
                        defaultValue:
                          "Pri viacerých jedlách na obrazovke môže prebiehať automatické prepínanie strán (paging), aby sa zobrazili všetky položky. Interval určuje, ako často sa stránka zmení.",
                      })}
                    </Alert>
                    <Grid container columnSpacing={2}>
                      <Grid item xs={12} sm="auto">
                        <Div
                          sx={{
                            ...fieldSx,
                            maxWidth: 200,
                            minWidth: 72,
                            width: "100%",
                          }}
                        >
                          <TextField
                            label={t(
                              "pages.dsokDailyMenu.pageRotationIntervalSec",
                              {
                                defaultValue: "Interval rotácie stránok (s)",
                              },
                            )}
                            type="number"
                            value={form.pageRotationIntervalSec ?? 8}
                            onChange={(e) =>
                              update(
                                "pageRotationIntervalSec",
                                Number(e.target.value) || 0,
                              )
                            }
                            size="small"
                            inputProps={{ min: MIN_INTERVAL_SEC }}
                            error={!!errors.pageRotationIntervalSec}
                            helperText={errors.pageRotationIntervalSec}
                            sx={{ width: "100%" }}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  s
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Div>
                      </Grid>
                    </Grid>
                  </Box>

                  <Box>
                    <Alert severity="info" sx={{ mb: 2 }}>
                      {t("pages.dsokDailyMenu.menuTypeCheckIntervalInfo", {
                        defaultValue:
                          "Ako často obrazovka kontroluje, ktorý typ menu (raňajky/obed/večera) sa má podľa času zobraziť. Minimum 5 minút.",
                      })}
                    </Alert>
                    <Grid container columnSpacing={2}>
                      <Grid item xs={12} sm="auto">
                        <Div
                          sx={{
                            ...fieldSx,
                            maxWidth: 220,
                            minWidth: 72,
                            width: "100%",
                          }}
                        >
                          <TextField
                            label={t(
                              "pages.dsokDailyMenu.menuTypeCheckIntervalMin",
                              {
                                defaultValue: "Interval kontroly typu menu (min)",
                              },
                            )}
                            type="number"
                            value={form.menuTypeCheckIntervalMin ?? 5}
                            onChange={(e) =>
                              update(
                                "menuTypeCheckIntervalMin",
                                Number(e.target.value) || 0,
                              )
                            }
                            size="small"
                            inputProps={{ min: MIN_MENU_TYPE_CHECK_MIN }}
                            error={!!errors.menuTypeCheckIntervalMin}
                            helperText={errors.menuTypeCheckIntervalMin}
                            sx={{ width: "100%" }}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  min
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Div>
                      </Grid>
                    </Grid>
                  </Box>

                  <Divider />

                  {/* Sekcia: Týždenné menu (LEN portrait) */}
                  <Box>
                    <Typography variant="h6" gutterBottom>
                      {t("pages.dsokDailyMenu.sectionWeeklyMenu", {
                        defaultValue: "Týždenné menu",
                      })}
                    </Typography>
                    <Alert severity="info" sx={{ mb: 2 }}>
                      {t("pages.dsokDailyMenu.sectionWeeklyMenuInfo", {
                        defaultValue:
                          "Keď je zapnuté, v nastavenom intervale sa na obrazovke strieda aktuálne denné menu (raňajky/obed/večera) s týždenným menu. Poznámka: táto funkcia sa uplatní IBA na obrazovke na výšku (portrait) — na obrazovke na šírku sa neprejaví (aj samotná implementácia sa týka len portrait obrazovky).",
                      })}
                    </Alert>
                    <Grid container columnSpacing={2}>
                      <Grid item {...gridItemProps}>
                        <Div sx={fieldSx}>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={!!form.weeklyMenuRotationEnabled}
                                onChange={(e) =>
                                  update(
                                    "weeklyMenuRotationEnabled",
                                    e.target.checked,
                                  )
                                }
                              />
                            }
                            label={t(
                              "pages.dsokDailyMenu.weeklyMenuRotationEnabled",
                              {
                                defaultValue: "Zobraziť týždenné menu",
                              },
                            )}
                          />
                        </Div>
                      </Grid>
                      {form.weeklyMenuRotationEnabled && (
                        <Grid item {...gridItemProps}>
                          <Div sx={fieldSx}>
                            <TextField
                              select
                              fullWidth
                              size="small"
                              label={t("pages.dsokDailyMenu.weeklyMenuType", {
                                defaultValue: "Typ menu (týždenný pohľad)",
                              })}
                              value={form.weeklyMenuType || "lunch"}
                              onChange={(e) =>
                                update("weeklyMenuType", e.target.value)
                              }
                            >
                              {DAILY_MENU_TYPES.map((typ) => (
                                <MenuItem key={typ.code} value={typ.code}>
                                  {typ.name}
                                </MenuItem>
                              ))}
                            </TextField>
                          </Div>
                        </Grid>
                      )}
                      {form.weeklyMenuRotationEnabled && (
                        <Grid item xs={12} sm="auto">
                          <Div
                            sx={{
                              ...fieldSx,
                              maxWidth: 220,
                              minWidth: 72,
                              width: "100%",
                            }}
                          >
                            <TextField
                              label={t(
                                "pages.dsokDailyMenu.weeklyMenuRotationIntervalSec",
                                {
                                  defaultValue: "Interval prepínania (s)",
                                },
                              )}
                              type="number"
                              value={form.weeklyMenuRotationIntervalSec ?? 60}
                              onChange={(e) =>
                                update(
                                  "weeklyMenuRotationIntervalSec",
                                  Number(e.target.value) || 0,
                                )
                              }
                              size="small"
                              inputProps={{ min: MIN_INTERVAL_SEC }}
                              error={!!errors.weeklyMenuRotationIntervalSec}
                              helperText={errors.weeklyMenuRotationIntervalSec}
                              sx={{ width: "100%" }}
                              InputProps={{
                                endAdornment: (
                                  <InputAdornment position="end">
                                    s
                                  </InputAdornment>
                                ),
                              }}
                            />
                          </Div>
                        </Grid>
                      )}
                    </Grid>
                  </Box>
                </Stack>
              </TabPanel>

              <TabPanel
                value="display"
                sx={{ pb: 1, pr: 0, maxHeight: "60vh", overflow: "auto" }}
              >
                <Alert severity="info" sx={{ mb: 2 }}>
                  {t("pages.dsokDailyMenu.sectionDisplayInfo", {
                    defaultValue:
                      "Tu nastavíte, ako sa má denné menu zobrazovať na obrazovke: koľko jedál sa zobrazí na jednej stránke (landscape/portrait), či zobrazovať nedostupné produkty, či ich zoradiť na koniec a či zobrazovať alergény.",
                  })}
                </Alert>

                <Box sx={{ mb: 3 }}>
                  <FormControl component="fieldset" sx={{ mb: 2 }}>
                    <FormLabel component="legend" sx={{ mb: 1 }}>
                      {t("pages.dsokDailyMenu.productDisplayTypeLandscape", {
                        defaultValue: "Typ zobrazenia produktov (landscape)",
                      })}
                    </FormLabel>
                    <RadioGroup
                      row
                      value={
                        form.productDisplayTypeLandscape === "grid"
                          ? "grid"
                          : "list"
                      }
                      onChange={(e) =>
                        update("productDisplayTypeLandscape", e.target.value)
                      }
                    >
                      <FormControlLabel
                        value="list"
                        control={<Radio />}
                        label={
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <ViewListIcon fontSize="small" />
                            <span>
                              {t("pages.dsokDailyMenu.productDisplayTypeList", {
                                defaultValue: "Zoznam",
                              })}
                            </span>
                          </Stack>
                        }
                        sx={{ mr: 3 }}
                      />
                      <FormControlLabel
                        value="grid"
                        control={<Radio />}
                        label={
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <GridViewIcon fontSize="small" />
                            <span>
                              {t("pages.dsokDailyMenu.productDisplayTypeGrid", {
                                defaultValue: "Mriežka",
                              })}
                            </span>
                          </Stack>
                        }
                      />
                    </RadioGroup>
                  </FormControl>

                  <FormControl component="fieldset">
                    <FormLabel component="legend" sx={{ mb: 1 }}>
                      {t("pages.dsokDailyMenu.productDisplayTypePortrait", {
                        defaultValue: "Typ zobrazenia produktov (portrait)",
                      })}
                    </FormLabel>
                    <RadioGroup
                      row
                      value={
                        form.productDisplayTypePortrait === "grid"
                          ? "grid"
                          : "list"
                      }
                      onChange={(e) =>
                        update("productDisplayTypePortrait", e.target.value)
                      }
                    >
                      <FormControlLabel
                        value="list"
                        control={<Radio />}
                        label={
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <ViewListIcon fontSize="small" />
                            <span>
                              {t("pages.dsokDailyMenu.productDisplayTypeList", {
                                defaultValue: "Zoznam",
                              })}
                            </span>
                          </Stack>
                        }
                        sx={{ mr: 3 }}
                      />
                      <FormControlLabel
                        value="grid"
                        control={<Radio />}
                        label={
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <GridViewIcon fontSize="small" />
                            <span>
                              {t("pages.dsokDailyMenu.productDisplayTypeGrid", {
                                defaultValue: "Mriežka",
                              })}
                            </span>
                          </Stack>
                        }
                      />
                    </RadioGroup>
                  </FormControl>
                </Box>

                <Divider sx={{ mb: 2 }} />

                <Box sx={{ mb: 3 }}>
                  <FormControl component="fieldset" sx={{ minWidth: 260 }}>
                    <FormLabel component="legend" sx={{ mb: 1 }}>
                      {t("pages.dsokDailyMenu.contentRotation", {
                        defaultValue: "Otočenie obsahu (portrait)",
                      })}
                    </FormLabel>
                    <Select
                      size="small"
                      value={form.contentRotation || "none"}
                      onChange={(e) =>
                        update("contentRotation", e.target.value)
                      }
                    >
                      <MenuItem value="none">
                        {t("pages.dsokDailyMenu.contentRotationNone", {
                          defaultValue: "Žiadne",
                        })}
                      </MenuItem>
                      <MenuItem value="cw">
                        {t("pages.dsokDailyMenu.contentRotationCw", {
                          defaultValue: "90° doprava",
                        })}
                      </MenuItem>
                      <MenuItem value="ccw">
                        {t("pages.dsokDailyMenu.contentRotationCcw", {
                          defaultValue: "90° doľava",
                        })}
                      </MenuItem>
                    </Select>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 1, maxWidth: 480 }}
                    >
                      {t("pages.dsokDailyMenu.contentRotationHint", {
                        defaultValue:
                          "Platí iba pre portrait obrazovku. Použite, ak je fyzicky otočená, ale softvér v nej nevie natívne rotovať (stále zobrazuje na šírku, aj keď je panel na výšku). Landscape sa nikdy neotáča. Zmena sa prejaví po kliknutí na „Obnoviť obrazovky“ alebo po automatickom obnovení.",
                      })}
                    </Typography>
                  </FormControl>
                </Box>

                <Divider sx={{ mb: 2 }} />

                <Grid container columnSpacing={2}>
                  <Grid item xs={12} sm="auto">
                    <Div
                      sx={{
                        ...fieldSx,
                        maxWidth: 200,
                        minWidth: 72,
                        width: "100%",
                      }}
                    >
                      <TextField
                        label={t(
                          "pages.dsokDailyMenu.maxMealsPerPageLandscape",
                          {
                            defaultValue: "Max. jedál (landscape)",
                          },
                        )}
                        type="number"
                        value={form.maxMealsPerPageLandscape ?? 5}
                        onChange={(e) =>
                          update(
                            "maxMealsPerPageLandscape",
                            Number(e.target.value) ?? 1,
                          )
                        }
                        size="small"
                        inputProps={{ min: MIN_MEALS_PER_PAGE }}
                        error={!!errors.maxMealsPerPageLandscape}
                        helperText={errors.maxMealsPerPageLandscape}
                        sx={{ width: "100%" }}
                      />
                    </Div>
                  </Grid>
                  <Grid item xs={12} sm="auto">
                    <Div
                      sx={{
                        ...fieldSx,
                        maxWidth: 200,
                        minWidth: 72,
                        width: "100%",
                      }}
                    >
                      <TextField
                        label={t(
                          "pages.dsokDailyMenu.maxMealsPerPagePortrait",
                          {
                            defaultValue: "Max. jedál (portrait)",
                          },
                        )}
                        type="number"
                        value={form.maxMealsPerPagePortrait ?? 6}
                        onChange={(e) =>
                          update(
                            "maxMealsPerPagePortrait",
                            Number(e.target.value) ?? 1,
                          )
                        }
                        size="small"
                        inputProps={{ min: MIN_MEALS_PER_PAGE }}
                        error={!!errors.maxMealsPerPagePortrait}
                        helperText={errors.maxMealsPerPagePortrait}
                        sx={{ width: "100%" }}
                      />
                    </Div>
                  </Grid>
                  <Grid item {...gridItemProps}>
                    <Div sx={{ ...fieldSx, width: "100%" }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={!!form.showUnavailableProducts}
                            onChange={(e) =>
                              update("showUnavailableProducts", e.target.checked)
                            }
                          />
                        }
                        label={t("pages.dsokDailyMenu.showUnavailableProducts", {
                          defaultValue: "Zobrazovať nedostupné produkty",
                        })}
                      />
                    </Div>
                  </Grid>
                  <Grid item {...gridItemProps}>
                    <Div sx={{ ...fieldSx, width: "100%" }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={!!form.sortUnavailableToEnd}
                            onChange={(e) =>
                              update("sortUnavailableToEnd", e.target.checked)
                            }
                          />
                        }
                        label={t("pages.dsokDailyMenu.sortUnavailableToEnd", {
                          defaultValue: "Nedostupné na koniec",
                        })}
                      />
                    </Div>
                  </Grid>
                  <Grid item {...gridItemProps}>
                    <Div sx={{ ...fieldSx, width: "100%" }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={!!form.showDescription}
                            onChange={(e) =>
                              update("showDescription", e.target.checked)
                            }
                          />
                        }
                        label={t("pages.dsokDailyMenu.showDescription", {
                          defaultValue: "Zobrazovať popis",
                        })}
                      />
                    </Div>
                  </Grid>
                  <Grid item {...gridItemProps}>
                    <Div sx={{ ...fieldSx, width: "100%" }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={!!form.showAllergens}
                            onChange={(e) =>
                              update("showAllergens", e.target.checked)
                            }
                          />
                        }
                        label={t("pages.dsokDailyMenu.showAllergens", {
                          defaultValue: "Zobrazovať alergény",
                        })}
                      />
                    </Div>
                  </Grid>
                  <Grid item {...gridItemProps}>
                    <Div sx={{ ...fieldSx, width: "100%" }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={!!form.showPrices}
                            onChange={(e) =>
                              update("showPrices", e.target.checked)
                            }
                          />
                        }
                        label={t("pages.dsokDailyMenu.showPrices", {
                          defaultValue: "Zobrazovať ceny",
                        })}
                      />
                    </Div>
                  </Grid>
                </Grid>
              </TabPanel>

              <TabPanel
                value="ticker"
                sx={{ pb: 1, pr: 0, maxHeight: "60vh", overflow: "auto" }}
              >
                <Grid container columnSpacing={2}>
                  <Grid item xs={12} sm="auto">
                    <Div sx={{ ...fieldSx, width: "100%" }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={!!form.tickerEnabled}
                            onChange={(e) =>
                              update("tickerEnabled", e.target.checked)
                            }
                          />
                        }
                        label={t("pages.dsokDailyMenu.tickerEnabled", {
                          defaultValue: "Info text zapnutý",
                        })}
                      />
                    </Div>
                  </Grid>
                  <Grid item xs={12} sm="auto">
                    <Div
                      sx={{
                        ...fieldSx,
                        maxWidth: 200,
                        minWidth: 72,
                        width: "100%",
                      }}
                    >
                      <TextField
                        label={t("pages.dsokDailyMenu.tickerSpeed", {
                          defaultValue: "Rýchlosť info textu (px/s)",
                        })}
                        type="number"
                        value={form.tickerSpeed ?? 60}
                        onChange={(e) =>
                          update("tickerSpeed", Number(e.target.value) || 60)
                        }
                        size="small"
                        sx={{ width: "100%" }}
                        inputProps={{ min: 1 }}
                      />
                    </Div>
                  </Grid>
                </Grid>
                <Alert severity="info" sx={{ mt: 2, mb: 2 }}>
                  {t("pages.dsokDailyMenu.tickerTextsDescription", {
                    defaultValue:
                      "Tento text sa zobrazuje v spodnej časti obrazovky.",
                  })}
                </Alert>
                <Grid container columnSpacing={2}>
                  {languages.map((lang) => (
                    <Grid item xs={12} key={lang.code}>
                      <Div sx={{ ...fieldSx, width: "100%" }}>
                        <TextField
                          label={`${t("pages.dsokDailyMenu.tickerText", { defaultValue: "Info text" })} (${lang.label})`}
                          value={
                            (form.tickerTexts && form.tickerTexts[lang.code]) ||
                            ""
                          }
                          onChange={(e) =>
                            updateNested(
                              "tickerTexts",
                              lang.code,
                              e.target.value,
                            )
                          }
                          multiline
                          rows={2}
                          fullWidth
                          size="small"
                        />
                      </Div>
                    </Grid>
                  ))}
                </Grid>
              </TabPanel>

              <TabPanel
                value="menuTypes"
                sx={{ pb: 1, pr: 0, maxHeight: "60vh", overflow: "auto" }}
              >
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  {t("pages.dsokDailyMenu.menuTypesHint", {
                    defaultValue:
                      "Nastavte časový rozsah (od – do), kedy sa má na obrazovke zobraziť daný typ menu.",
                  })}
                </Typography>
                <Grid container spacing={2}>
                  {DAILY_MENU_TYPES.map((typ) => {
                    const cfg = form.menuTypes?.[typ.code] || {};
                    const setTime = (field, value) =>
                      updateNested("menuTypes", typ.code, {
                        ...(form.menuTypes?.[typ.code] || {}),
                        [field]: value,
                      });
                    return (
                      <Grid item xs={12} key={typ.code}>
                        <Grid
                          container
                          columnSpacing={2}
                          rowSpacing={1}
                          alignItems="center"
                        >
                          <Grid item xs={12} sm={3}>
                            <Typography variant="subtitle1">
                              {typ.name}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm="auto">
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={cfg.active !== false}
                                  onChange={(e) =>
                                    setTime("active", e.target.checked)
                                  }
                                />
                              }
                              label={t("pages.dsokDailyMenu.menuTypeActive", {
                                defaultValue: "Aktívny",
                              })}
                            />
                          </Grid>
                          <Grid item xs={6} sm="auto">
                            <TextField
                              type="time"
                              size="small"
                              label={t("pages.dsokDailyMenu.timeFrom", {
                                defaultValue: "Čas od",
                              })}
                              value={cfg.from || ""}
                              onChange={(e) => setTime("from", e.target.value)}
                              InputLabelProps={{ shrink: true }}
                              disabled={cfg.active === false}
                              sx={{ maxWidth: 140 }}
                            />
                          </Grid>
                          <Grid item xs={6} sm="auto">
                            <TextField
                              type="time"
                              size="small"
                              label={t("pages.dsokDailyMenu.timeTo", {
                                defaultValue: "Čas do",
                              })}
                              value={cfg.to || ""}
                              onChange={(e) => setTime("to", e.target.value)}
                              InputLabelProps={{ shrink: true }}
                              disabled={cfg.active === false}
                              sx={{ maxWidth: 140 }}
                            />
                          </Grid>
                        </Grid>
                      </Grid>
                    );
                  })}
                </Grid>
              </TabPanel>

              <TabPanel
                value="webExport"
                sx={{ pb: 1, pr: 0, maxHeight: "60vh", overflow: "auto" }}
              >
                <Stack spacing={3}>
                  {/* ---------- Web ---------- */}
                  {enableWebExport && (
                    <Box>
                      <Typography variant="h5" gutterBottom>
                        {t("pages.dsokDailyMenu.exportSectionWeb", {
                          defaultValue: "Web",
                        })}
                      </Typography>
                      <Alert severity="info" sx={{ mb: 2 }}>
                        {t("pages.dsokDailyMenu.webExportInfo", {
                          defaultValue:
                            "Publikovanie sa spúšťa tlačidlom v zozname denných menu – vždy naraz vygeneruje 6 súborov: breakfast_1/2.json, lunch_1/2.json, dinner_1/2.json (_2 = aktuálny týždeň, _1 = nasledujúci týždeň). Kombinácie bez existujúceho menu sa preskočia.",
                        })}
                      </Alert>
                      <Typography variant="h6" gutterBottom>
                        {t("pages.dsokDailyMenu.webExportDaysTitle", {
                          defaultValue: "Dni na export",
                        })}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 2 }}
                      >
                        {t("pages.dsokDailyMenu.webExportDaysHint", {
                          defaultValue:
                            "Vyberte, ktoré dni sa majú objaviť v exportovanom JSON súbore. Platí pre všetky typy menu (raňajky/obed/večera).",
                        })}
                      </Typography>
                      <FormGroup row>
                        {WEB_EXPORT_DAYS.map((day) => (
                          <FormControlLabel
                            key={day.code}
                            control={
                              <Checkbox
                                checked={
                                  form.webExport?.days?.[day.code] !== false
                                }
                                onChange={(e) =>
                                  updateNested("webExport", "days", {
                                    ...(form.webExport?.days || {}),
                                    [day.code]: e.target.checked,
                                  })
                                }
                              />
                            }
                            label={t(day.labelKey, {
                              defaultValue: day.defaultLabel,
                            })}
                            sx={{ mr: 3 }}
                          />
                        ))}
                      </FormGroup>
                    </Box>
                  )}

                  {enableWebExport && <Divider />}

                  {/* ---------- CSV ---------- */}
                  <Box>
                    <Typography variant="h5" gutterBottom>
                      {t("pages.dsokDailyMenu.exportSectionCsv", {
                        defaultValue: "CSV",
                      })}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 2 }}
                    >
                      {t("pages.dsokDailyMenu.csvExportHint", {
                        defaultValue:
                          "Tieto nastavenia sa použijú pri exporte menu do CSV (tlačidlo „CSV“ v detaile denného menu).",
                      })}
                    </Typography>
                    <FormGroup>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={form.csvExport?.exportPrice !== false}
                            onChange={(e) =>
                              updateNested(
                                "csvExport",
                                "exportPrice",
                                e.target.checked,
                              )
                            }
                          />
                        }
                        label={t("pages.dsokDailyMenu.csvExportPrice", {
                          defaultValue: "Export ceny",
                        })}
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={form.csvExport?.exportWeight !== false}
                            onChange={(e) =>
                              updateNested(
                                "csvExport",
                                "exportWeight",
                                e.target.checked,
                              )
                            }
                          />
                        }
                        label={t("pages.dsokDailyMenu.csvExportWeight", {
                          defaultValue: "Export váhy",
                        })}
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={form.csvExport?.exportDescription !== false}
                            onChange={(e) =>
                              updateNested(
                                "csvExport",
                                "exportDescription",
                                e.target.checked,
                              )
                            }
                          />
                        }
                        label={t("pages.dsokDailyMenu.csvExportDescription", {
                          defaultValue: "Export popisu",
                        })}
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={form.csvExport?.exportDayName !== false}
                            onChange={(e) =>
                              updateNested(
                                "csvExport",
                                "exportDayName",
                                e.target.checked,
                              )
                            }
                          />
                        }
                        label={t("pages.dsokDailyMenu.csvExportDayName", {
                          defaultValue: "Export názvu dňa",
                        })}
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={form.csvExport?.exportId !== false}
                            onChange={(e) =>
                              updateNested(
                                "csvExport",
                                "exportId",
                                e.target.checked,
                              )
                            }
                          />
                        }
                        label={t("pages.dsokDailyMenu.csvExportId", {
                          defaultValue: "Export ID",
                        })}
                      />
                    </FormGroup>

                    <Box sx={{ mt: 3, maxWidth: 380 }}>
                      <Typography variant="h6" gutterBottom>
                        {t("pages.dsokDailyMenu.csvNameComposition", {
                          defaultValue: "Tvorba názvu jedla",
                        })}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 1.5 }}
                      >
                        {t("pages.dsokDailyMenu.csvNameCompositionHint", {
                          defaultValue:
                            "Váha sa pridá do zátvorky pred názov, alergény do zátvorky za názov.",
                        })}
                      </Typography>
                      <FormControl fullWidth size="small">
                        <Select
                          multiple
                          displayEmpty
                          value={csvNameComposition}
                          onChange={(e) => {
                            const val =
                              typeof e.target.value === "string"
                                ? e.target.value.split(",")
                                : e.target.value;
                            updateNested("csvExport", "nameComposition", val);
                          }}
                          renderValue={(selected) =>
                            !selected || selected.length === 0
                              ? t("pages.dsokDailyMenu.csvNameCompositionNone", {
                                  defaultValue: "Iba názov jedla",
                                })
                              : selected
                                  .map((code) => {
                                    const p = CSV_NAME_PARTS.find(
                                      (x) => x.code === code,
                                    );
                                    return p
                                      ? t(p.labelKey, {
                                          defaultValue: p.defaultLabel,
                                        })
                                      : code;
                                  })
                                  .join(", ")
                          }
                        >
                          {CSV_NAME_PARTS.map((p) => (
                            <MenuItem key={p.code} value={p.code}>
                              <Checkbox
                                checked={csvNameComposition.includes(p.code)}
                              />
                              <ListItemText
                                primary={t(p.labelKey, {
                                  defaultValue: p.defaultLabel,
                                })}
                              />
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  </Box>
                </Stack>
              </TabPanel>

              <TabPanel value="integrations">
                {(() => {
                  const mc = form?.integrations?.mailchimp || {};
                  const setMc = (field, value) =>
                    update("integrations", {
                      ...(form?.integrations || {}),
                      mailchimp: { ...mc, [field]: value },
                    });
                  const handleTest = async () => {
                    try {
                      const res = await mailchimpTestConnection();
                      const d = res?.data || {};
                      enqueueSnackbar(
                        d.listName
                          ? `Mailchimp OK — audience „${d.listName}" (${
                              d.memberCount ?? "?"
                            } odberateľov)`
                          : "Mailchimp spojenie OK",
                        { variant: "success" },
                      );
                    } catch (e) {
                      enqueueSnackbar(
                        e?.response?.data?.error ||
                          e?.response?.data?.reason ||
                          "Test spojenia zlyhal",
                        { variant: "error" },
                      );
                    }
                  };
                  return (
                    <Stack spacing={2} sx={{ maxWidth: 640 }}>
                      <Typography variant="h6">Mailchimp</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Rozposielanie týždenného menu odberateľom cez Mailchimp.
                        Odosielanie sa spúšťa manuálne tlačidlom priamo v editore
                        denného menu.
                      </Typography>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={mc.enabled === true}
                            onChange={(e) => setMc("enabled", e.target.checked)}
                          />
                        }
                        label="Povoliť Mailchimp"
                      />
                      <TextField
                        size="small"
                        fullWidth
                        type="password"
                        label="Mailchimp API kľúč"
                        value={mc.apiKey || ""}
                        onChange={(e) => setMc("apiKey", e.target.value)}
                        placeholder={
                          mc.apiKeyConfigured
                            ? `Uložený (${mc.apiKeyMasked || "••••"}) — vyplň len pri zmene`
                            : "napr. abcdef123456-us21"
                        }
                        helperText={
                          mc.apiKeyConfigured
                            ? "Kľúč je uložený. Ponechaj prázdne, ak ho nechceš meniť."
                            : "Vlož Mailchimp API kľúč (obsahuje príponu dátového centra, napr. -us21)."
                        }
                      />
                      <TextField
                        size="small"
                        fullWidth
                        label="Data center (voliteľné)"
                        value={mc.serverPrefix || ""}
                        onChange={(e) => setMc("serverPrefix", e.target.value)}
                        placeholder="napr. us21 (prázdne = odvodí sa z kľúča)"
                      />
                      <TextField
                        size="small"
                        fullWidth
                        label="Audience / List ID"
                        value={mc.audienceId || ""}
                        onChange={(e) => setMc("audienceId", e.target.value)}
                        placeholder="ID zoznamu odberateľov v Mailchimpe"
                      />
                      <TextField
                        size="small"
                        fullWidth
                        label="Odosielateľ (meno)"
                        value={mc.fromName || ""}
                        onChange={(e) => setMc("fromName", e.target.value)}
                        placeholder="napr. Catering Piešťany"
                        error={String(mc.fromName || "").includes("@")}
                        helperText={
                          String(mc.fromName || "").includes("@")
                            ? "Meno odosielateľa nesmie byť e-mail (bez @). E-mail patrí do Reply-to."
                            : "Zobrazené meno odosielateľa (nie e-mail)."
                        }
                      />
                      <TextField
                        size="small"
                        fullWidth
                        label="Reply-to email"
                        value={mc.replyTo || ""}
                        onChange={(e) => setMc("replyTo", e.target.value)}
                        placeholder="napr. info@cateringpiestany.sk"
                        helperText="E-mailová adresa pre odpovede odberateľov."
                      />
                      <TextField
                        size="small"
                        fullWidth
                        label="Predmet e-mailu"
                        value={mc.subjectTemplate || ""}
                        onChange={(e) =>
                          setMc("subjectTemplate", e.target.value)
                        }
                        placeholder="Jedálny lístok {{TYZDEN}}"
                        helperText="Premenné: {{TYZDEN}} (rozsah dátumov), {{DATUM}} (dnešný dátum)."
                      />
                      <TextField
                        size="small"
                        fullWidth
                        multiline
                        minRows={4}
                        label="Úvodný text e-mailu (nad menu)"
                        value={mc.introText || ""}
                        onChange={(e) => setMc("introText", e.target.value)}
                        placeholder={
                          "Dobrý deň,\nna základe Vašej registrácie na www.cateringpiestany.sk Vám zasielame jedálny lístok na ďalší týždeň.\nStiahnuť/vytlačiť: https://cateringpiestany.sk/sk/obedove-menu?j=*|DATE:Y-m-d-H-i|*"
                        }
                        helperText="Odkazy (https://…, www.…) budú klikacie. Mailchimp merge tagy (napr. *|DATE:Y-m-d-H-i|*) zostanú zachované."
                      />
                      <Box>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={handleTest}
                        >
                          Test pripojenia
                        </Button>
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        Ulož zmeny tlačidlom nižšie. Kľúč sa ukladá bezpečne na
                        serveri a do prehliadača sa nevracia.
                      </Typography>
                    </Stack>
                  );
                })()}
              </TabPanel>
            </Stack>
          </TabContext>

          <Div
            sx={{
              mt: 2,
              mb: 1,
              mx: 2,
              gap: "4px",
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <Button size="normal" onClick={handleBack}>
              {t("buttons.cancel", { defaultValue: "Späť" })}
            </Button>
            <Button
              size="normal"
              variant="contained"
              onClick={handleSave}
              disabled={saving}
            >
              {t("buttons.save", { defaultValue: "Uložiť" })}
            </Button>
          </Div>
        </Box>
      </Div>
    </>
  );
};

export default DsokDailyMenuScreenSettings;
